import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Wallet, MessageCircle, Loader2, Landmark } from "lucide-react";
import { useCart, formatPrice, API } from "../context/CartContext";
import { buildWaMessage } from "../components/WhatsAppFloat";

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, products, totalLabel, currency, config, clearCart } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", shippingAddress: "", sameAddress: true });
  const [method, setMethod] = useState("bank");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (config.midtrans_enabled && !document.getElementById("midtrans-snap")) {
      const s = document.createElement("script");
      s.id = "midtrans-snap";
      s.src = config.midtrans_is_production
        ? "https://app.midtrans.com/snap/snap.js"
        : "https://app.sandbox.midtrans.com/snap/snap.js";
      s.setAttribute("data-client-key", config.midtrans_client_key);
      document.body.appendChild(s);
    }
  }, [config]);

  const payload = () => ({
    items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    customer: {
      name: form.name, email: form.email, phone: form.phone || null,
      address: form.address || null,
      shipping_address: (form.sameAddress ? form.address : form.shippingAddress) || null,
    },
    origin_url: window.location.origin,
  });

  const pay = async () => {
    if (method === "whatsapp") {
      const msg = buildWaMessage(t, items, products, currency);
      window.open(`https://wa.me/${config.whatsapp_number}?text=${encodeURIComponent(msg)}`, "_blank");
      return;
    }
    if (!form.name || !form.email) { toast.error(t("checkout.fillRequired")); return; }
    const totalKg = items.reduce((s, i) => {
      const p = products.find((pr) => pr.product_id === i.product_id);
      return s + (p ? p.weight_kg * i.quantity : 0);
    }, 0);
    if (method === "stripe" && totalKg < 25000) { toast.error(t("products.exportMin")); return; }
    setBusy(true);
    try {
      if (method === "stripe") {
        const { data } = await axios.post(`${API}/checkout/stripe`, payload());
        window.location.href = data.checkout_url;
      } else if (method === "bank") {
        const { data } = await axios.post(`${API}/checkout/bank-transfer`, payload());
        clearCart();
        navigate(`/payment/success?order_id=${data.order_id}&gateway=bank`);
      } else if (method === "midtrans") {
        const { data } = await axios.post(`${API}/checkout/midtrans`, payload());
        window.snap.pay(data.snap_token, {
          onSuccess: () => { clearCart(); navigate(`/payment/success?order_id=${data.order_id}&gateway=midtrans`); },
          onPending: () => { clearCart(); navigate(`/payment/success?order_id=${data.order_id}&gateway=midtrans`); },
          onError: () => toast.error(t("checkout.payError")),
          onClose: () => {},
        });
        setBusy(false);
      }
    } catch {
      toast.error(t("checkout.payError"));
      setBusy(false);
    }
  };

  const methods = [
    { key: "bank", icon: Landmark, title: t("checkout.bankTitle"), desc: t("checkout.bankDesc"), enabled: true },
    { key: "stripe", icon: CreditCard, title: t("checkout.stripeTitle"), desc: t("checkout.stripeDesc"), enabled: true },
    { key: "midtrans", icon: Wallet, title: t("checkout.midtransTitle"),
      desc: config.midtrans_enabled ? t("checkout.midtransDesc") : `${t("checkout.midtransDesc")} — ${t("checkout.midtransSoon")}`,
      enabled: config.midtrans_enabled },
    { key: "whatsapp", icon: MessageCircle, title: t("checkout.waTitle"), desc: t("checkout.waDesc"), enabled: true },
  ];

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-brand-cream flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-brand-brown font-semibold">{t("cart.empty")}</p>
        <button data-testid="checkout-back-empty" onClick={() => navigate("/")}
          className="px-6 py-3 rounded-full bg-brand-orange text-white font-bold text-sm uppercase">{t("payment.home")}</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream py-10 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <button data-testid="checkout-back" onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm font-bold text-brand-brown hover:text-brand-dark transition-colors mb-6">
          <ArrowLeft size={16} className="rtl:rotate-180" /> {t("cart.continue")}
        </button>
        <h1 className="font-heading text-5xl text-brand-dark uppercase mb-10">{t("checkout.title")}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-8">
            <div className="bg-white rounded-3xl p-8 border border-brand-green/15">
              <h2 className="font-heading text-2xl text-brand-dark uppercase mb-5">{t("checkout.contact")}</h2>
              <div className="space-y-4">
                <input data-testid="checkout-name-input" value={form.name} placeholder={t("checkout.name")}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange" />
                <input data-testid="checkout-email-input" type="email" value={form.email} placeholder={t("checkout.email")}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange" />
                <input data-testid="checkout-phone-input" value={form.phone} placeholder={t("checkout.phone")}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange" />
                <textarea data-testid="checkout-address-input" rows="2" value={form.address} placeholder={t("checkout.billingAddress")}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange resize-none" />
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input data-testid="checkout-same-address-checkbox" type="checkbox" checked={form.sameAddress}
                    onChange={(e) => setForm({ ...form, sameAddress: e.target.checked })}
                    className="w-5 h-5 accent-[#F58220] rounded" />
                  <span className="text-sm font-semibold text-brand-brown">{t("checkout.sameAddress")}</span>
                </label>
                {!form.sameAddress && (
                  <textarea data-testid="checkout-shipping-address-input" rows="2" value={form.shippingAddress} placeholder={t("checkout.shippingAddress")}
                    onChange={(e) => setForm({ ...form, shippingAddress: e.target.value })}
                    className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange resize-none" />
                )}
              </div>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-brand-green/15">
              <h2 className="font-heading text-2xl text-brand-dark uppercase mb-5">{t("checkout.method")}</h2>
              <div className="space-y-3">
                {methods.map((m) => (
                  <button key={m.key} data-testid={`payment-method-${m.key}`} disabled={!m.enabled}
                    onClick={() => setMethod(m.key)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-start transition-colors ${
                      method === m.key ? "border-brand-orange bg-brand-cream" : "border-brand-green/15 bg-white"} ${
                      !m.enabled ? "opacity-50 cursor-not-allowed" : "hover:border-brand-orange/50"}`}>
                    <m.icon size={22} className="text-brand-orange shrink-0" />
                    <div>
                      <div className="font-bold text-sm text-brand-dark">{m.title}</div>
                      <div className="text-xs text-brand-brown">{m.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 border border-brand-green/15 sticky top-24">
              <h2 className="font-heading text-2xl text-brand-dark uppercase mb-5">{t("checkout.summary")}</h2>
              <div className="space-y-3">
                {items.map((i) => {
                  const p = products.find((pr) => pr.product_id === i.product_id);
                  if (!p) return null;
                  return (
                    <div key={i.product_id} className="flex justify-between text-sm">
                      <span className="text-brand-brown font-semibold">Agrodyke {p.weight_kg} kg × {i.quantity}</span>
                      <span className="font-bold text-brand-dark">{formatPrice(p, currency, i.quantity)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-brand-green/15 mt-5 pt-5 flex justify-between items-center">
                <span className="font-bold text-brand-brown">{t("cart.total")}</span>
                <span data-testid="checkout-total" className="font-heading text-3xl text-brand-dark">{totalLabel}</span>
              </div>
              <p data-testid="checkout-tax-note" className="text-[11px] text-brand-brown/70 font-semibold italic mt-2">
                * {t("products.taxNote")}
              </p>
              <button data-testid="checkout-pay-button" onClick={pay} disabled={busy}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors disabled:opacity-60">
                {busy ? (<><Loader2 size={18} className="animate-spin" /> {t("checkout.processing")}</>) : t("checkout.pay")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
